// packages/console/src/lib/preset.ts
//
// Bridges the real FT-3 preset model (services/orchestrator) to the display
// vocabulary the output-profile panel uses, and computes the derived rows
// (R3): keyframe interval, resolution, formats-out, and the estimated output
// size. The prototype's illustrative constants (crf x 0.24, 5.6 EVE) are NOT
// ported; the estimate is computed honestly from the preset's real rate
// parameters and the selected source's probe duration, and is absent (em dash)
// whenever an input it needs is unknown.

import type { Container, Preset, RateControl, Scaling, VideoCodec } from "../api/jobs";
import { EM_DASH } from "./format";

// Container display labels, upper-cased for the chips (server stores lowercase).
export const CONTAINERS: { id: Container; label: string }[] = [
  { id: "mp4", label: "MP4" },
  { id: "mov", label: "MOV" },
  { id: "hls", label: "HLS" },
  { id: "dash", label: "DASH" },
  { id: "cmaf", label: "CMAF" },
  { id: "webm", label: "WebM" },
];

export const RATE_CONTROLS: { id: RateControl; label: string }[] = [
  { id: "crf", label: "CRF" },
  { id: "vbr", label: "VBR" },
  { id: "cbr", label: "CBR" },
];

// Video codec chips. The server invariant "webm requires av1" is enforced in the
// panel (webm disables h264/hevc, and choosing webm forces av1).
export const CODECS: { id: VideoCodec; label: string }[] = [
  { id: "h264", label: "H.264" },
  { id: "hevc", label: "HEVC" },
  { id: "av1", label: "AV1" },
  { id: "vp8", label: "VP8" },
  { id: "vp9", label: "VP9" },
  { id: "svtav1", label: "SVT-AV1" },
  { id: "libaom", label: "libaom" },
];

// Standard ABR ladder steps for quick-add. The server stores an explicit
// width/height per rung; these are the common 16:9 resolutions.
export const LADDER_STEPS: { name: string; width: number; height: number }[] = [
  { name: "240p", width: 426, height: 240 },
  { name: "360p", width: 640, height: 360 },
  { name: "480p", width: 854, height: 480 },
  { name: "540p", width: 960, height: 540 },
  { name: "720p", width: 1280, height: 720 },
  { name: "1080p", width: 1920, height: 1080 },
  { name: "1440p", width: 2560, height: 1440 },
  { name: "2160p", width: 3840, height: 2160 },
];

// Encoder speed: the prototype shows fast/medium/slow; the real model is
// p1..p7 (p1 slowest/best, p7 fastest). Map the three visible chips onto three
// representative points so the control stays faithful while writing valid
// values to the service.
export const SPEED_CHIPS: { id: "p6" | "p4" | "p2"; label: string }[] = [
  { id: "p6", label: "fast" },
  { id: "p4", label: "medium" },
  { id: "p2", label: "slow" },
];

export function speedChipFor(speed: string): "p6" | "p4" | "p2" {
  if (speed === "p7" || speed === "p6" || speed === "p5") return "p6";
  if (speed === "p4") return "p4";
  return "p2";
}

// PROFILE_MAP: per-codec profile options as { label (display), id (backend
// -profile:v value) }. The backend maps id -> -profile:v (and SKIPs the flag
// when the concrete encoder rejects it). "" id = encoder default. Levels apply
// only to h264/hevc; other codecs show "n/a".
export interface ProfileOption {
  id: string;
  label: string;
}
export const PROFILE_MAP: Record<VideoCodec, ProfileOption[]> = {
  h264: [
    { id: "baseline", label: "Baseline" },
    { id: "main", label: "Main" },
    { id: "high", label: "High" },
    { id: "high10", label: "High 10" },
  ],
  hevc: [
    { id: "main", label: "Main" },
    { id: "main10", label: "Main 10" },
    { id: "main444-8", label: "Main 4:4:4" },
  ],
  av1: [
    { id: "main", label: "Main" },
    { id: "high", label: "High" },
    { id: "professional", label: "Professional" },
  ],
  svtav1: [
    { id: "main", label: "Main" },
    { id: "high", label: "High" },
    { id: "professional", label: "Professional" },
  ],
  libaom: [
    { id: "main", label: "Main" },
    { id: "high", label: "High" },
    { id: "professional", label: "Professional" },
  ],
  vp8: [{ id: "", label: "Default" }],
  vp9: [
    { id: "0", label: "Profile 0" },
    { id: "1", label: "Profile 1" },
    { id: "2", label: "Profile 2" },
    { id: "3", label: "Profile 3" },
  ],
};

// Codecs that accept a -level; every other codec shows "n/a" and stays "auto".
export const LEVEL_CODECS: VideoCodec[] = ["h264", "hevc"];
export const LEVELS: string[] = ["auto", "3.0", "3.1", "3.2", "4.0", "4.1", "4.2", "5.0", "5.1", "5.2", "6.0", "6.1", "6.2"];

// profileOptionsFor returns the option list for a codec (never empty).
export function profileOptionsFor(codec: VideoCodec): ProfileOption[] {
  return PROFILE_MAP[codec] ?? [{ id: "", label: "Default" }];
}

// defaultProfileFor is the seed profile a new/changed rung gets for a codec: the
// design defaults to the "High"-tier option (index 2) when the codec has one,
// otherwise the first option.
export function defaultProfileFor(codec: VideoCodec): string {
  const o = profileOptionsFor(codec);
  return (o.length > 2 ? o[2] : o[0]).id;
}

// profileLabelFor maps a stored profile id back to its display label.
export function profileLabelFor(codec: VideoCodec, id: string): string {
  const o = profileOptionsFor(codec).find((p) => p.id === id);
  return o ? o.label : id || "Default";
}

// levelsApply reports whether the level select is meaningful for a codec.
export function levelsApply(codec: VideoCodec): boolean {
  return LEVEL_CODECS.includes(codec);
}

// AUDIO_CODECS drive the audio-codec selects. "" = container-derived default; a
// concrete choice is validated against the delivery formats (opus invalid in
// mov, aac invalid in webm) before it is written.
export const AUDIO_CODECS: { id: "aac" | "opus"; label: string }[] = [
  { id: "aac", label: "AAC" },
  { id: "opus", label: "Opus" },
];

// audioCodecAllowsFormat mirrors the video codecAllowsFormat guard for audio:
// opus is invalid in mov, aac is invalid in webm. "" (container-derived) is
// always allowed.
export function audioCodecAllowsFormat(f: Container, ac: "aac" | "opus" | ""): boolean {
  if (ac === "opus") return f !== "mov";
  if (ac === "aac") return f !== "webm";
  return true;
}

// AV1_CODECS is the set of codec ids that produce an AV1 bitstream (any encoder),
// mirroring the backend av1Family. webm carries only av1/vp8/vp9.
const AV1_CODECS: VideoCodec[] = ["av1", "svtav1", "libaom"];

// codecAllowsFormat mirrors the backend formatAllowedForCodec: vp8/vp9 go only
// in webm; webm carries only av1/vp8/vp9; every other container (mp4/mov/hls/
// dash/cmaf) carries h264/hevc/av1. This drives the inline Packaging select's
// option set so it never offers a container the profile's codec cannot carry.
export function codecAllowsFormat(f: Container, codec: VideoCodec): boolean {
  if (codec === "vp8" || codec === "vp9") return f === "webm";
  if (f === "webm") return AV1_CODECS.includes(codec);
  return true;
}

// packagingOptionsFor returns the delivery containers a preset's codec can carry,
// in a stable display order (mp4, hls, dash, cmaf, mov, webm). It is the option
// set for the inline Packaging select. The list is never empty: every codec
// carries at least one container.
const PACKAGING_ORDER: Container[] = ["mp4", "hls", "dash", "cmaf", "mov", "webm"];
export function packagingOptionsFor(codec: VideoCodec): Container[] {
  return PACKAGING_ORDER.filter((f) => codecAllowsFormat(f, codec));
}

// defaultPackagingFor picks the default Packaging for a preset: its primary
// container when that container is valid for the codec, else the first valid
// container. This is the value the Packaging select resets to when the Profile
// changes to one with a different codec.
export function defaultPackagingFor(preset: Preset): Container {
  const opts = packagingOptionsFor(preset.videoCodec);
  if (opts.includes(preset.container)) return preset.container;
  return opts[0] ?? "mp4";
}

// scalingLabel reads a scaling id as the short display label used in summaries.
export function scalingLabel(s?: Scaling): string {
  return s === "fill" ? "Fill · crop" : s === "stretch" ? "Stretch" : "Fit · pad";
}

// codecLabel renders the codec-neutral name the way the panel reads it.
export function codecLabel(codec: VideoCodec): string {
  switch (codec) {
    case "h264":
      return "H.264";
    case "hevc":
      return "HEVC";
    case "av1":
      return "AV1";
    case "vp8":
      return "VP8";
    case "vp9":
      return "VP9";
    case "svtav1":
      return "SVT-AV1";
    case "libaom":
      return "libaom";
  }
}

// resolutionLabel reads the top rung of the ladder (the largest output), or the
// em dash when the ladder is empty.
export function resolutionLabel(preset: Preset): string {
  if (preset.ladder.length === 0) return EM_DASH;
  const top = preset.ladder.reduce((a, b) => (a.width * a.height >= b.width * b.height ? a : b));
  return `${top.width}x${top.height}`;
}

// keyframeFrames is the keyframe interval in frames. The service stores
// gopLength directly in FRAMES (unlike the prototype's gop-seconds x fps), so
// the derived row reports gopLength verbatim.
export function keyframeLabel(preset: Preset): string {
  return `${preset.gopLength} frames`;
}

// targetKbps returns the effective target bitrate in kbps for size estimation,
// or null when the mode is CRF (quality-targeted, size not directly derivable
// from a bitrate). CRF output size cannot be honestly estimated without an
// encoder model, so it renders as the em dash rather than a fabricated figure.
export function targetKbps(preset: Preset): number | null {
  if (preset.rateControl === "crf") return null;
  return preset.bitrateKbps && preset.bitrateKbps > 0 ? preset.bitrateKbps : null;
}

// estOutputLabel derives the estimated output size (R3) from the preset's real
// bitrate and the source probe duration. Bytes = bitrate(bps) * seconds / 8,
// summed conceptually over the ladder is out of scope without per-rung
// bitrates, so this reports the top-rung target. Returns the em dash when the
// duration or a usable bitrate is unknown.
export function estOutputLabel(preset: Preset, durationSeconds: number | null): string {
  const kbps = targetKbps(preset);
  if (kbps === null || durationSeconds === null || durationSeconds <= 0) return EM_DASH;
  const bytes = (kbps * 1000 * durationSeconds) / 8;
  const gb = bytes / (1024 * 1024 * 1024);
  return `${gb.toFixed(2)} GB`;
}
